import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LanguageSelector } from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { Zap, Droplets, ChevronLeft, FileText } from 'lucide-react';

export const TermsConditionsPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-primary">
                <Zap className="h-5 w-5" strokeWidth={2.5} />
                <Droplets className="h-5 w-5 text-blue-500" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-xl tracking-tight">Safi Energy</span>
            </Link>
            <LanguageSelector variant="ghost" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ChevronLeft className="h-4 w-4" />
          {t('nav_application')}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <FileText className="h-8 w-8 text-primary" />
          <h1 className="text-4xl font-bold tracking-tight uppercase">SafiEco Terms and Conditions</h1>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Effective Date: February 2024</p>
          
          <p className="text-lg">
            These Terms and Conditions govern your use of the SafiEco mobile application and services.
          </p>
          
          <p className="font-semibold text-primary">
            By using SafiEco, you agree to comply with these Terms.
          </p>

          <Section title="1. About SafiEco">
            <p>SafiEco is a digital platform developed by Safi Energy that allows users to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Report electricity outages</li>
              <li>Report water supply interruptions</li>
              <li>Receive outage alerts</li>
              <li>Monitor infrastructure disruptions</li>
            </ul>
            <p className="mt-2 text-muted-foreground">
              SafiEco provides informational services and does not directly control electricity or water infrastructure.
            </p>
          </Section>

          <Section title="2. User Eligibility">
            <p>To use SafiEco you must:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Be at least 16 years old</li>
              <li>Provide accurate information</li>
              <li>Use the platform in accordance with applicable laws</li>
            </ul>
          </Section>

          <Section title="3. User Responsibilities">
            <p>Users agree not to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Submit false outage reports</li>
              <li>Use the platform for illegal activities</li>
              <li>Harass or abuse other users</li>
              <li>Attempt to hack or disrupt the service</li>
              <li>Upload harmful or malicious content</li>
            </ul>
            <p className="mt-2 font-medium">Safi Energy may suspend accounts violating these rules.</p>
          </Section>

          <Section title="4. Accuracy of Information">
            <p>SafiEco relies partly on community-submitted reports.</p>
            <p className="mt-2 text-muted-foreground">
              While Safi Energy aims to provide accurate information, it cannot guarantee that all outage reports are correct or complete.
            </p>
          </Section>

          <Section title="5. Service Availability">
            <p>Safi Energy does not guarantee uninterrupted availability of the SafiEco platform.</p>
            <p className="mt-2">Services may be temporarily unavailable due to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Maintenance</li>
              <li>Network issues</li>
              <li>Infrastructure failures</li>
              <li>Government or regulatory requirements</li>
            </ul>
          </Section>

          <Section title="6. Intellectual Property">
            <p>All content, branding, software, and technology used in SafiEco are the property of Safi Energy.</p>
            <p className="mt-2">Users may not reproduce, copy, or distribute any part of the application without permission.</p>
          </Section>

          <Section title="7. Limitation of Liability">
            <p>Safi Energy is not responsible for:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Damages caused by electricity or water outages</li>
              <li>Decisions made based on outage information</li>
              <li>Third-party service failures</li>
            </ul>
            <p className="mt-2 font-medium">The application provides informational assistance only.</p>
          </Section>

          <Section title="8. Account Termination">
            <p>Safi Energy reserves the right to suspend or terminate accounts that:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Violate these Terms</li>
              <li>Misuse the platform</li>
              <li>Endanger system security</li>
            </ul>
          </Section>

          <Section title="9. Governing Law">
            <p>These Terms are governed by the laws of the Democratic Republic of Congo.</p>
            <p className="mt-2">
              Any disputes shall be resolved in the competent courts of Lubumbashi or other applicable jurisdictions in the DRC.
            </p>
          </Section>

          <Section title="10. Changes to Terms">
            <p>Safi Energy may modify these Terms at any time.</p>
            <p>Users will be notified through the application.</p>
          </Section>

          <div className="mt-8 p-6 bg-card border border-border rounded-lg">
            <p className="font-semibold text-lg">Contact</p>
            <p className="text-muted-foreground">Safi Energy</p>
            <p className="text-muted-foreground">Lubumbashi, Democratic Republic of Congo</p>
            <p className="text-muted-foreground">Email: info@safienergy.org</p>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-border bg-card mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">{t('footer_copyright')}</p>
        </div>
      </footer>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mt-8">
    <h2 className="text-2xl font-bold text-foreground mb-4">{title}</h2>
    <div className="text-muted-foreground space-y-2">{children}</div>
  </div>
);
